import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useSitStores, useStores} from "../../../../stores";
import {generatePath, useNavigate, useParams} from "react-router";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    TextField
} from "@mui/material";
import {InitiativeFinancialForm} from "./initiativeFinancialForm";
import {sitRoutes} from "../../../routing/sitRoutes";
import {InitiativePlaningFrom} from "./InitiativePlaningFrom";
import {InitiativeSuccessStepFrom} from "./InitiativeSuccessStepFrom";
import {
    AttachFile,
    CallSplit,
    Category,
    Check,
    ChevronLeft,
    Flag,
    Group,
    History,
    InfoOutlined,
    Inventory2,
    Payments,
    Schedule,
    UploadFile,
    Verified
} from "@mui/icons-material";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {InitiativeScopeForm} from "./initiativeScopeForm";
import {InitiativeGeneralForm} from "./initiativeGeneralForm";
import {InitiativeSkuForm} from "./initiativeSkuForm";
import {InitiativePeopleForm} from "./initiativePeopleForm";
import {InitiativeAttachmentForm} from "./initiativeAttachmentForm";
import {InitiativeAuditLogForm} from "./initiativeAuditLogForm";
import {InitiativeSplitForm} from "./initiativeSplitForm.tsx";
import {styled} from "@mui/material/styles";
import {InitiativeEvidenceForm} from "./initiativeEvidenceForm";
import type {Initiative} from "../../../ApiTypes";
import {CancellationReasonValues} from "../../../ApiTypes";
import {cancellationReasonLabel, sitFieldLabel} from "../../../sitLabels";
import i18n from "../../../../i18n/i18n";
import {SitSection, SitDoiStepper, SitTrafficDot} from "../../../components/design/SitDesignAtoms";
// import {InitiativeKpiForm} from "./initiativeKpiForm";
// import {InitiativeTeamForm} from "./initiativeTeamForm";

// See https://mui.com/material-ui/react-button/#file-upload
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const InitiativeForm: React.FC = observer(() => {
    const {initiativeListStore, initiativeInstanceStore, attachmentStore, sitApi} = useSitStores();
    const {p} = useStores();
    const navigate = useNavigate();
    const params = useParams();
    const initiativeId: number | 'new' = 'initiativeId' in params ? parseInt(params['initiativeId'] ?? '') || 'new' : 'new';
    const approvalParam = 'approval' in params && params['approval'] === 'approval';
    const [isLoading, setIsLoading] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isLoadingApproval, setIsLoadingApproval] = React.useState(false);
    const [deactivateOpen, setDeactivateOpen] = React.useState(false);
    const [cancelReason, setCancelReason] = React.useState<number>(1);

    useEffect(() => {
        if (initiativeId === 'new')
            initiativeInstanceStore.setInstance({});
        else
            sitApi.getInitiative(initiativeId).then((resp) => {
                initiativeInstanceStore.setInstance(resp.data, approvalParam);
            }).catch((_reason) => {
                navigate(generatePath(sitRoutes.sit_initiatives));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiativeId]);

    const goBack = () => {
        // Release possible lock, clear the form and return to the list.
        if (!initiativeInstanceStore.disabled)
            initiativeInstanceStore.releaseLock();
        initiativeInstanceStore.setInstance({});
        navigate(generatePath(sitRoutes.sit_initiatives));
    };

    return (
        <div className="sit-formscreen">
            <form onSubmit={(event) => {
                event.preventDefault();
                setIsLoading(true);
                initiativeInstanceStore.createOrUpdate()?.then(() => {
                    initiativeInstanceStore.setSaveErrors(undefined);
                    initiativeInstanceStore.setInstance({});
                    initiativeListStore.reloadData();
                    navigate(generatePath(sitRoutes.sit_initiatives));
                }).catch(error => {
                    initiativeInstanceStore.setSaveErrors(error.response.data);
                }).finally(() => {
                    setIsLoading(false);
                });
            }}>
                <div className="sit-form-head">
                    <button type="button" className="ac-icon-btn" title="Back to initiatives" onClick={goBack}>
                        <ChevronLeft/>
                    </button>
                    <div className="sit-form-head-mid">
                        <div className="sit-form-eyebrow">
                            {initiativeId === 'new'
                                ? "New initiative · Initiative details"
                                : `SIT-${initiativeId} · Initiative's details`}
                        </div>
                        <input className="sit-form-titleinput" placeholder="Untitled initiative"
                               disabled={initiativeInstanceStore.disabled}
                               value={initiativeInstanceStore.instance.title ?? ''}
                               onChange={e => initiativeInstanceStore.title = e.target.value}/>
                    </div>

                    {initiativeId !== 'new' &&
                        <SitTrafficDot initiative={initiativeInstanceStore.instance as Initiative}/>}
                    {initiativeInstanceStore.instance.approved &&
                        <span className="sit-tag hue-good">Approved</span>}
                    {initiativeInstanceStore.instance.cancellation_reason != null &&
                        <span className="sit-tag hue-bad" title={cancellationReasonLabel(initiativeInstanceStore.instance.cancellation_reason)}>
                            {i18n.t('pill.deactivated', {ns: 'sit'})}
                        </span>}

                    {initiativeId !== 'new' &&
                        <button type="button" className="ac-btn"
                                title={!initiativeInstanceStore.instance.locked_by ? '' : 'Locked by ' + initiativeInstanceStore.instance.locked_by.name}
                                disabled={
                                    initiativeInstanceStore.disabled &&
                                    (initiativeInstanceStore.instance.locked_by != null) &&
                                    (initiativeInstanceStore.instance.locked_by!.id !== initiativeInstanceStore.authStore.sitUserProfile?.id)
                                }
                                onClick={() => {
                                    if (initiativeInstanceStore.disabled) initiativeInstanceStore.getLock();
                                    else initiativeInstanceStore.releaseLock();
                                }}>
                            {initiativeInstanceStore.disabled ? <LockIcon fontSize="small"/> : <LockOpenIcon fontSize="small"/>}
                            {initiativeInstanceStore.disabled ? 'Locked' : 'Unlocked'}
                        </button>}

                    {!initiativeInstanceStore.disabled && !initiativeInstanceStore.readyForApproval && <>
                        {initiativeId !== 'new' && initiativeInstanceStore.instance.cancellation_reason == null &&
                            <button type="button" className="ac-btn ac-btn--danger" onClick={() => setDeactivateOpen(true)}>
                                Deactivate
                            </button>}
                        <button type="button" className="ac-btn" onClick={goBack}>Cancel</button>
                        <button type="submit" className="ac-btn ac-btn--primary"
                                disabled={isLoading || initiativeInstanceStore.disabled}>
                            {isLoading ? <CircularProgress size={14}/> : <Check fontSize="small"/>}
                            {initiativeId === 'new' ? 'Add initiative' : 'Update initiative'}
                        </button>
                    </>}

                    {initiativeInstanceStore.readyForApproval && <>
                        <button type="button" className="ac-btn ac-btn--danger"
                                onClick={() => {
                                    initiativeInstanceStore.approve(false)?.then(() => {
                                        initiativeListStore.reloadApprovalData();
                                        navigate(generatePath(sitRoutes.sit_initiatives));
                                    });
                                }}>Reject
                        </button>
                        <button type="button" className="ac-btn ac-btn--primary"
                                onClick={() => {
                                    initiativeInstanceStore.approve(true)?.then(() => {
                                        initiativeListStore.reloadApprovalData();
                                        navigate(generatePath(sitRoutes.sit_initiatives));
                                    });
                                }}>Accept
                        </button>
                    </>}
                </div>

                <div className="sit-form-scroll">
                    <div className="sit-form-col">
                        <div className="sit-details">
                            <div className="sit-det-stepper">
                                <SitDoiStepper doi={initiativeInstanceStore.instance.doi_status ?? 1}
                                               approvalState={initiativeInstanceStore.instance.approved ? 'approved' : null}/>
                            </div>

                            <SitSection icon={<InfoOutlined/>} title="General information" tint="a">
                                <InitiativeGeneralForm/>
                            </SitSection>

                            <SitSection icon={<Category/>} title="Scope & classification" tint="b">
                                <InitiativeScopeForm/>
                            </SitSection>

                            <SitSection icon={<CallSplit/>} title="Saving split" tint="b"
                                        sub="For scope of multiple companies or categories, the savings need to be distributed among the participants.">
                                <InitiativeSplitForm/>
                            </SitSection>

                            <SitSection icon={<Group/>} title="Team & roles" tint="a">
                                <InitiativePeopleForm/>
                            </SitSection>

                            <SitSection icon={<Payments/>} title="Financials" tint="c">
                                <InitiativeFinancialForm/>
                            </SitSection>

                            <SitSection icon={<Schedule/>} title="Timeline & planning" tint="a">
                                <InitiativePlaningFrom/>
                            </SitSection>

                            {!p.p.sitHideSkuForm &&
                            <SitSection icon={<Inventory2/>} title="Related SKUs" tint="b">
                                <InitiativeSkuForm/>
                            </SitSection>}

                            <SitSection icon={<Flag/>} title="Key milestones" tint="a"
                                        sub="Key milestones that need to be achieved for realizing this initiative.">
                                <InitiativeSuccessStepFrom/>
                            </SitSection>

                            <SitSection icon={<Verified/>} title={sitFieldLabel('evidence')} tint="b"
                                        sub="External proof links (e.g. SharePoint) for audit.">
                                <InitiativeEvidenceForm/>
                            </SitSection>

                            {!initiativeInstanceStore.creating &&
                            <SitSection icon={<AttachFile/>} title="Attachments" tint="c"
                                        sub="Attach any documents, e.g., calculation of savings, presentations, etc."
                                        right={initiativeInstanceStore.instance.id && initiativeInstanceStore.instance.id > 0 &&
                                            <Button
                                                disabled={initiativeInstanceStore.disabled || isUploading}
                                                variant="outlined"
                                                component="label"
                                                role={undefined}
                                                startIcon={<UploadFile/>}>
                                                Upload Document
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    onChange={(event) => {
                                                        if (event.target.files && event.target.files.item(0) !== null) {
                                                            setIsUploading(true);
                                                            sitApi.createInitiativeAttachment(
                                                                initiativeInstanceStore.instance.id!, event.target.files.item(0)!
                                                            ).then(() => {
                                                                attachmentStore.initiativeAttachmentsRM.request({id: initiativeInstanceStore.instance.id!});
                                                                setIsUploading(false);
                                                            });
                                                        }
                                                    }}/>
                                            </Button>}>
                                <InitiativeAttachmentForm/>
                            </SitSection>}

                            {!initiativeInstanceStore.creating &&
                            <SitSection icon={<History/>} title="Activity log" tint="a"
                                        sub="Who changed what on this initiative, and when.">
                                <InitiativeAuditLogForm/>
                            </SitSection>}
                        </div>

                        {/*<Grid container spacing={3}>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Typography variant="h3" component="h3">KPI For Monitoring</Typography>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Grid container spacing={3} className="mt-2" justifyContent="end">*/}
                        {/*            <Grid>*/}
                        {/*                <Button*/}
                        {/*                    disabled={initiativeInstanceStore.disabled}*/}
                        {/*                    variant="outlined"*/}
                        {/*                    startIcon={<Add/>}*/}
                        {/*                    onClick={() => initiativeInstanceStore.kpiDialogOpen = true}>*/}
                        {/*                    New KPI for monitoring*/}
                        {/*                </Button>*/}
                        {/*            </Grid>*/}
                        {/*        </Grid>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={12}>*/}
                        {/*        <InitiativeKpiForm/>*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}

                        {initiativeInstanceStore.readyForApproval && <Grid container spacing={3}>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled
                                        multiline minRows={3}
                                        variant="outlined"
                                        label="Approval Notes"
                                        className="mt-3"
                                        value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}/>
                                </FormControl>
                            </Grid>
                        </Grid>}

                        {initiativeInstanceStore.save_errors && (initiativeInstanceStore.save_errors!.length > 0) &&
                            <Grid container spacing={3} className="mt-2">
                                <Grid size={12}>
                                    <Alert severity="error" color="error">
                                        Please fix the following and try again
                                        <List>
                                            {initiativeInstanceStore.save_errors.map((err_item, index) =>
                                                <ListItem key={index} disablePadding>
                                                    <ListItemText primary={err_item[0]} secondary={err_item[1]}/>
                                                </ListItem>
                                            )}
                                        </List>
                                    </Alert>
                                </Grid>
                            </Grid>}
                    </div>
                </div>
                {/*Approval */}
                <Dialog
                    open={initiativeInstanceStore.approval_dialog_open} aria-labelledby="dialog-title"
                    aria-describedby="dialog-description"
                    onClose={() => {
                        setIsLoadingApproval(false);
                        initiativeInstanceStore.approvalDialogOpen = false;
                    }}>
                    <DialogTitle id="dialog-title">
                        Approval Required
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="dialog-description">
                            Changing initiative status to
                            <b> "DOI5 - EBIT effective" </b>
                            requires approval from
                            <b> "{initiativeInstanceStore.instance.approver?.name}" </b>.
                            <br/>
                            Are you sure you want to submit this change?
                        </DialogContentText>
                        <Grid container className="mt-2">
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        multiline minRows={3}
                                        variant="outlined"
                                        label="Approval Notes"
                                        value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}
                                        onChange={(event) =>
                                            initiativeInstanceStore.description.approval_notes = event.target.value
                                        }/>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsLoadingApproval(false);
                            initiativeInstanceStore.approvalDialogOpen = false;
                        }}>cancel</Button>
                        <Button
                            variant="contained"
                            disabled={isLoadingApproval}
                            className="loading-button"
                            onClick={() => {
                                setIsLoadingApproval(true);
                                initiativeInstanceStore.createOrUpdate(true)?.then(() => {
                                    initiativeInstanceStore.setSaveErrors(undefined);
                                    initiativeListStore.reloadApprovalData();
                                }).catch(error => {
                                    initiativeInstanceStore.setSaveErrors(error.response.data);
                                }).finally(() => {
                                    setIsLoadingApproval(false);
                                    initiativeInstanceStore.approvalDialogOpen = false;
                                    initiativeListStore.reloadData();
                                    navigate(generatePath(sitRoutes.sit_initiatives));
                                });
                            }}>
                            Send for approval
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Deactivate / cancel — captures an InitiativeCancellationReason */}
                <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
                    <DialogTitle>Deactivate initiative</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Select a reason for cancelling this initiative. It will be flagged as
                            deactivated with the reason recorded.
                        </DialogContentText>
                        <FormControl fullWidth className="mt-3">
                            <TextField select label={sitFieldLabel('cancellationReason')} value={cancelReason}
                                       onChange={(e) => setCancelReason(Number(e.target.value))}>
                                {CancellationReasonValues.map(v =>
                                    <MenuItem key={v} value={v}>{cancellationReasonLabel(v)}</MenuItem>)}
                            </TextField>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                initiativeInstanceStore.cancellationReason = cancelReason;
                                initiativeInstanceStore.createOrUpdate()?.then(() => {
                                    initiativeInstanceStore.setSaveErrors(undefined);
                                    initiativeInstanceStore.setInstance({});
                                    initiativeListStore.reloadData();
                                    navigate(generatePath(sitRoutes.sit_initiatives));
                                }).catch(error => {
                                    initiativeInstanceStore.setSaveErrors(error.response.data);
                                }).finally(() => setDeactivateOpen(false));
                            }}>
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>
        </div>
    );
});